import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import DesignCanvas from '@/components/brand/DesignCanvas';
import { IMAGE_ROLES, PORTAL_CLINIC, autoImage, autoText, fetchPortalDesigns, type Lang } from '@/lib/brand-kit';
import { qk } from '@/lib/queries';
import { cn } from '@/lib/cn';

/** Designs KAF PRO shared with the clinic (owner 2026-10-24): previews drawn with the clinic's own
 *  kit by the same renderer the studio exports with. Read only: nothing here changes them. */
export default function PortalDesignsPage() {
  const q = useQuery({ queryKey: qk.portalDesigns, queryFn: fetchPortalDesigns });
  const [lang, setLang] = useState<Lang>('ar');
  if (q.isLoading) return <p className="text-sm text-muted">Loading...</p>;
  if (q.isError || !q.data) return <p className="text-sm text-red-400">Could not load the designs.</p>;
  const { kit, templates, client_name } = q.data;

  return (
    <div className="grid gap-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-white">Designs</h1>
          <p className="text-sm text-muted" dir="rtl">التصاميم</p>
          <p className="text-sm text-muted mt-1">Templates KAF PRO prepared for {client_name}, in your brand fonts and colours.</p>
        </div>
        <div className="flex gap-1">
          {(['ar', 'en'] as const).map((l) => (
            <button key={l} onClick={() => setLang(l)} data-testid={`portal-designs-${l}`}
              className={cn('px-3 h-8 rounded text-xs', lang === l ? 'bg-accent-500/20 text-white' : 'text-muted')}>{l === 'en' ? 'English' : 'العربية'}</button>
          ))}
        </div>
      </div>
      {templates.length === 0 ? (
        <Card><p className="text-sm text-muted">Nothing shared yet.</p></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((d) => {
            const t = d.template;
            const l: Lang = t.lang === 'both' ? lang : t.lang;
            const texts: Record<string, string> = {}; const images: Record<string, string> = {};
            for (const e of t.elements) {
              if (IMAGE_ROLES.includes(e.role)) images[e.id] = autoImage(kit, e, { lang: l });
              else if (e.role !== 'shape' && e.role !== 'icon') texts[e.id] = autoText(kit, e, { lang: l });
            }
            return (
              <Card key={d.id} data-testid="portal-design">
                <DesignCanvas clientId={PORTAL_CLINIC} kit={kit} template={t} lang={l} texts={texts} images={images} />
                <div className="mt-2 text-sm text-muted-strong" data-testid="portal-design-name">{d.name}</div>
                <div className="text-[11px] text-muted">{t.w} x {t.h}</div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
